import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {Alert, Button, Card, TextField} from '@betareader/design-system';
import { withTranslation, Trans } from 'react-i18next';
import { push } from '../../navigation';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

import genres from '../../shared/constants/genres';

import {
  updateUserProfile,
  updateUser,
  getUserProfile,
  refreshUserProfile,
} from '../../modules/user';

// utils
import { uxAnalyticsUtil, authUtil } from '../../utils';

import language from '../../config/language';
import DeleteAccountModal from './deleteAccount/deleteAccountModal';
import SocialProfiles from './userProfileBuilder/fields/socialProfiles';
import BlockedUsersList from './components/blockedUsersList';

const usernameRegex = /^[a-z0-9]+$/i;
const refererTokenKey = 'br-referer-token';

const ProfileGrid = styled.main`
  display: grid;
  gap: var(--br-space-4);
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const ProfileCard = styled(Card)`
  display: grid;
  gap: var(--br-space-3);
  padding: var(--br-space-4);
`;

const ChoiceGroup = styled.fieldset`
  border: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--br-space-3);
  margin: 0;
  padding: 0;
`;

export const Profile = ({
  user,
  userProfile,
  updatingUserProfile,

  updateUser,
  updateUserProfile,
  t,
}) => {
  const [profileChanged, setProfileChanged] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [refererToken, setRefererToken] = useState();
  const [genreOptions, setGenreOptions] = useState();
  const [email, setEmail] = useState(user?.email ?? '');
  const [emailError, setEmailError] = useState(undefined);
  const [emailVerified, setEmailVerified] = useState(user?.emailVerified ?? '');
  const [newUser, setNewUser] = useState(userProfile?.newUser ?? undefined);
  const [uid, setUid] = useState(userProfile?.uid);
  const [preferredGenres, setPreferredGenres] = useState(
    userProfile?.preferredGenres ?? undefined
  );
  const [displayName, setDisplayName] = useState(
    userProfile?.displayName ?? ''
  );
  const [username, setUsername] = useState(userProfile?.username ?? '');
  const [usernameError, setUsernameError] = useState(undefined);
  const [role, setRole] = useState(userProfile?.role ?? undefined);
  const [receiveCompanyUpdates, setReceiveCompanyUpdates] = useState(
    userProfile?.emailPreferences?.receiveCompanyUpdates ?? false
  );
  const [receiveBookActivityUpdates, setReceiveBookActivityUpdates] = useState(
    userProfile?.emailPreferences?.receiveBookActivityUpdates ?? false
  );
  const [receiveBetaSuggestions, setReceiveBetaSuggestions] = useState(
    userProfile?.emailPreferences?.receiveBetaSuggestions ?? false
  );
  const [receiveMessageUpdates, setReceiveMessageUpdates] = useState(
    userProfile?.emailPreferences?.receiveMessageUpdates ?? false
  );
  const [verificationEmailMessage, setVerificationnEmailMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatNewPassword, setRepeatNewPassword] = useState('');
  const [socialProfiles, setSocialProfiles] = useState(
    userProfile?.socialProfiles ?? {}
  );
  const [socialProfileErrors, setSocialProfileErrors] = useState({});

  const passwordLongEnough = useCallback(
    () => newPassword?.length > 5,
    [newPassword]
  );
  const passwordMatch = useCallback(
    () => newPassword === repeatNewPassword,
    [newPassword, repeatNewPassword]
  );

  const validateSocialProfileUrl = useCallback((platform, url) => {
    if (!url) return true;

    const patterns = {
      goodreads: /^https?:\/\/(www\.)?goodreads\.com\/user\/show\//,
      tome: /^https?:\/\/(www\.)?tome\.app\//,
      instagram: /^https?:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_.]+\/?$/,
      tiktok: /^https?:\/\/(www\.)?tiktok\.com\/@[a-zA-Z0-9_.]+\/?$/,
    };

    return patterns[platform]?.test(url) ?? false;
  }, []);

  const handleSocialProfileChange = useCallback(
    newSocialProfiles => {
      setSocialProfiles(newSocialProfiles);
      setProfileChanged(true);

      // Validate URLs
      const errors = {};
      Object.keys(newSocialProfiles).forEach(platform => {
        const url = newSocialProfiles[platform];
        if (url && !validateSocialProfileUrl(platform, url)) {
          errors[platform] = true;
        }
      });
      setSocialProfileErrors(errors);

      // Only save if there are no errors - we'll call saveProfile directly inside the function
      if (Object.keys(errors).length === 0) {
        // Call saveProfile via timeout like other profile changes
        clearTimeout(saveProfileTimeout.current);
        saveProfileTimeout.current = setTimeout(async () => {
          setSavingProfile(true);
          const profileData = {};
          if (preferredGenres !== undefined) {
            profileData.preferredGenres = preferredGenres;
          }
          if (displayName !== undefined) {
            profileData.displayName = displayName;
          }
          if (username !== undefined) {
            profileData.username = username;
          }
          if (role !== undefined) {
            profileData.role = role;
          }
          if (newSocialProfiles !== undefined) {
            profileData.socialProfiles = newSocialProfiles;
          }
          const idToken = await authUtil.getFreshIdToken();
          updateUserProfile(idToken, profileData);
        }, 1500);
      }
    },
    [
      validateSocialProfileUrl,
      preferredGenres,
      displayName,
      username,
      role,
      updateUserProfile,
    ]
  );

  const saveProfileTimeout = useRef();

  const location = useLocation();

  // on first load
  useEffect(() => {
    // track page view
    uxAnalyticsUtil.trackPageView(
      location.pathname + location.hash + location.search
    );

    // set initial values
    const localStorageToken = localStorage.getItem(refererTokenKey);
    if (localStorageToken) {
      localStorage.removeItem(refererTokenKey);
    }
    const genreOptions = genres.map(genre => ({
      key: genre,
      value: genre,
      text: genre,
    }));
    setRefererToken(refererToken);
    setGenreOptions(genreOptions);
  }, []);

  // whenever the user or userprofile props change
  useEffect(() => {
    if (!profileChanged || savingProfile) {
      setEmail(user?.email ?? undefined);
      setEmailVerified(user?.emailVerified ?? undefined);
      setPreferredGenres(userProfile?.preferredGenres ?? undefined);
      setUid(userProfile?.uid ?? undefined);
      setNewUser(userProfile?.newUser ?? undefined);
      setDisplayName(userProfile?.displayName ?? undefined);
      setUsername(userProfile?.username ?? undefined);
      setRole(userProfile?.role ?? undefined);
      setReceiveCompanyUpdates(
        userProfile?.emailPreferences?.receiveCompanyUpdates ?? false
      );
      setReceiveBookActivityUpdates(
        userProfile?.emailPreferences?.receiveBookActivityUpdates ?? false
      );
      setReceiveBetaSuggestions(
        userProfile?.emailPreferences?.receiveBetaSuggestions ?? false
      );
      setReceiveMessageUpdates(
        userProfile?.emailPreferences?.receiveMessageUpdates ?? false
      );
      setSavingProfile(false);
    }
  }, [user, userProfile]);

  const isUsernameInvalid = username =>
    !username ||
    username.trim().length === 0 ||
    username.trim().length > 20 ||
    username.trim().length < 3 ||
    !username.match(usernameRegex);

  const usernameChanged = event => {
    const newName = event.target.value;
    setUsername(newName);
    setProfileChanged(true);
    setUsernameError(isUsernameInvalid(newName));
  };

  const saveUsername = async event => {
    event.preventDefault();
    if (isUsernameInvalid(username)) return;
    const idToken = await authUtil.getFreshIdToken();
    updateUserProfile(idToken, { username }, success => {
      if (!success) {
        // if we failed, reset username to the previous value
        setUsername(userProfile.username);
      }
    });
  };

  const roleChanged = async event => {
    const nextRole = event.target.value;
    setRole(nextRole);
    const idToken = await authUtil.getFreshIdToken();
    updateUserProfile(idToken, { role: nextRole }, success => {
      if (!success) {
        // if we failed, reset username to the previous value
        toast.error(t('SomethingWentWrong'));
        setRole(userProfile.role);
      }
    });
  };

  const emailChanged = event => {
    setEmail(event.target.value);
    setProfileChanged(true);
  };

  const updateEmail = event => {
    event.preventDefault();
    if (user && email && email !== user.email) {
      // verifyBeforeUpdateEmail sends the verification link to the NEW
      // address first. The Firebase account email only switches once the
      // user clicks that link — so a typo in the new address does not
      // lock the user out of their account.
      const actionCodeSettings = {
        url: `${window.location.origin}/account/profile`,
        handleCodeInApp: false,
      };
      user.verifyBeforeUpdateEmail(email, actionCodeSettings).then(
        function () {
          setEmailError(undefined);
          updateUser(user);
        },
        function (error) {
          setEmailError(error.message);
        }
      );
    }
  };

  const receiveCompanyUpdatesChanged = async event => {
    const checked = event.target.checked;
    setReceiveCompanyUpdates(checked);
    const idToken = await authUtil.getFreshIdToken();
    const updatedProfile = {
      emailPreferences: {...userProfile.emailPreferences},
    };
    updatedProfile.emailPreferences.receiveCompanyUpdates = checked;
    updatedProfile.emailPreferences.receiveCompanyUpdatesText = t(
      'ReceiveCompanyUpdates'
    );
    updateUserProfile(idToken, updatedProfile);
  };

  const receiveBookActivityUpdatesChanged = async event => {
    const checked = event.target.checked;
    setReceiveBookActivityUpdates(checked);
    const idToken = await authUtil.getFreshIdToken();
    const updatedProfile = {
      emailPreferences: {...userProfile.emailPreferences},
    };
    updatedProfile.emailPreferences.receiveBookActivityUpdates = checked;
    updatedProfile.emailPreferences.receiveBookActivityUpdatesText = t(
      'ReceiveBookActivityUpdates'
    );
    updateUserProfile(idToken, updatedProfile);
  };

  const receiveBetaSuggestionsChanged = async event => {
    const checked = event.target.checked;
    setReceiveBetaSuggestions(checked);
    const idToken = await authUtil.getFreshIdToken();
    const updatedProfile = {
      emailPreferences: {...userProfile.emailPreferences},
    };
    updatedProfile.emailPreferences.receiveBetaSuggestions = checked;
    updatedProfile.emailPreferences.receiveBetaSuggestionsText =
      t('ReceiveBookEmails');
    updateUserProfile(idToken, updatedProfile);
  };

  const receiveMessageUpdatesChanged = async event => {
    const checked = event.target.checked;
    setReceiveMessageUpdates(checked);
    const idToken = await authUtil.getFreshIdToken();
    const updatedProfile = {
      emailPreferences: {...userProfile.emailPreferences},
    };
    updatedProfile.emailPreferences.receiveMessageUpdates = checked;
    updateUserProfile(idToken, updatedProfile);
  };

  const newPasswordChanged = (event, data) => {
    const thePassword = data.value;
    setNewPassword(thePassword);
  };

  const repeatNewPasswordChanged = (event, data) => {
    const thePassword = data.value;
    setRepeatNewPassword(thePassword);
  };

  const updatePassword = () => {
    if (passwordLongEnough() && passwordMatch() && user !== undefined) {
      user
        .updatePassword(newPassword)
        .then(() => {
          setNewPassword(undefined);
          setRepeatNewPassword(undefined);
        })
        .catch(error => {
          console.log('failed to update password');
          uxAnalyticsUtil.trackException({
            description: `failed to update password ${error.message}`,
            fatal: false,
          });
        });
    }
  };

  const saveProfile = () => {
    clearTimeout(saveProfileTimeout.current);
    saveProfileTimeout.current = setTimeout(async () => {
      setSavingProfile(true);
      const profileData = {};
      if (preferredGenres !== undefined) {
        profileData.preferredGenres = preferredGenres;
      }
      if (displayName !== undefined) {
        profileData.displayName = displayName;
      }
      if (username !== undefined) {
        profileData.username = username;
      }
      if (role !== undefined) {
        profileData.role = role;
      }
      if (socialProfiles !== undefined) {
        profileData.socialProfiles = socialProfiles;
      }
      const idToken = await authUtil.getFreshIdToken();
      updateUserProfile(idToken, profileData);
    }, 1500);
  };

  const handleLanguageChange = async event => {
    const idToken = await authUtil.getFreshIdToken();
    updateUserProfile(idToken, { language: event.target.value });
  };

  // render
  if (!user || !userProfile) return null;

  return (
    <>
      {email === null && (
        <Alert variant='warning'>
          <h2>{t('WeNeedYourEmail')}</h2>
          <p>{t('MissingEmailMsg')}</p>
        </Alert>
      )}
      {emailError !== undefined && (
        <Alert variant='error'>{emailError}</Alert>
      )}
      <ProfileGrid>
        <section>
          <h1>{t('Settings')}</h1>
          <ProfileCard>
            <form onSubmit={updateEmail}>
              <TextField id='profile-email' type='email' label={t('Email')}
                placeholder={t('Email')} value={email || ''} onChange={emailChanged}
                aria-invalid={email === null || email === '' || emailError !== undefined} />
              {email !== userProfile.email && (
                <Button type='submit' size='small' aria-label={t('SaveEmail')}>
                  {t('Save')}
                </Button>
              )}
            </form>
            <form onSubmit={saveUsername}>
              <TextField id='profile-username' label={t('Username')}
                placeholder={t('Username')} value={username || ''} onChange={usernameChanged}
                aria-invalid={Boolean(usernameError)} />
              {usernameError && <p role='alert'>{t('InvalidUsername')}</p>}
              {username !== userProfile.username && (
                <Button type='submit' size='small' disabled={Boolean(usernameError) || updatingUserProfile?.includes('username')}>
                  {t('Save')}
                </Button>
              )}
            </form>
            <ChoiceGroup>
              <legend>{t('Role')}</legend>
              {[
                ['WRITER', 'RoleWriter'],
                ['READER', 'RoleReader'],
                ['BOTH', 'RoleBoth'],
              ].map(([value, label]) => (
                <label key={value}>
                  <input type='radio' name='profile-role' value={value}
                    checked={role === value} onChange={roleChanged} />
                  {t(label)}
                </label>
              ))}
            </ChoiceGroup>
            <label className='br-field' htmlFor='profile-language'>
              <span className='br-field__label'>{t('Language')}</span>
              <select id='profile-language' className='br-field__input'
                value={userProfile.language || 'en'} onChange={handleLanguageChange}>
                {language.languageOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.text}</option>
                ))}
              </select>
            </label>
            {!!userProfile.language && !['en', 'se', 'pt', 'sv'].includes(userProfile.language) && (
              <Trans i18nKey='BetaLanguageMessage'>
                <em>
                  We are working on improved language support, and are super grateful for any help we can get.
                  If you find any errors on the site, please contact us at{' '}
                  <a href='mailto:hello@betareader.io'>hello@betareader.io</a>.
                </em>
              </Trans>
            )}
          </ProfileCard>
        </section>
        <section>
          <h2>{t('Email')}</h2>
          <ProfileCard>
            {[
              ['ReceiveCompanyUpdates', receiveCompanyUpdates, receiveCompanyUpdatesChanged],
              ['ReceiveBookActivityUpdates', receiveBookActivityUpdates, receiveBookActivityUpdatesChanged],
              ['ReceiveBookEmails', receiveBetaSuggestions, receiveBetaSuggestionsChanged],
              ['ReceiveMessageUpdates', receiveMessageUpdates, receiveMessageUpdatesChanged],
            ].map(([label, checked, onChange]) => (
              <label key={label}>
                <input type='checkbox' checked={Boolean(checked)} onChange={onChange} />
                {t(label)}
              </label>
            ))}
          </ProfileCard>
          <ProfileCard>
            <h2>{t('SocialProfiles')}</h2>
            <SocialProfiles socialProfiles={socialProfiles}
              onChange={handleSocialProfileChange} errors={socialProfileErrors} />
          </ProfileCard>
          <ProfileCard><DeleteAccountModal /></ProfileCard>
          <ProfileCard><BlockedUsersList /></ProfileCard>
        </section>
      </ProfileGrid>
    </>
  );
};

// redux stuff
const mapStateToProps = state => ({
  user: state.user.user,
  userProfile: state.user.userProfile,
  updatingUserProfile: state.user.updatingUserProfile,
  socialProfiles: state.user.socialProfiles,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateUserProfile,
      updateUser,
      getUserProfile,
      refreshUserProfile,
      changePage: newPage => push(newPage),
    },
    dispatch
  );

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(Profile))
);
